import type {
  AssistantAnswer,
  AssistantCard,
  AssistantSurface,
} from '../types/intelligence'
import { listings } from '../data/housing'
import {
  myApplications as admissionApplications,
  programmeById,
  universityProfileById,
} from '../data/admissions'
import { myOrders, serviceProviders } from '../data/marketplace'
import { transactions } from '../data/finance'
import { deriveBalances } from './ledger'
import { courses } from '../data/academics'
import { opportunities } from '../data/career'
import { groups } from '../data/community'
import { defaultPreferences } from '../data/roommates'
import { orderStateLabel } from '../types/marketplace'
import { naira } from './format'
import { CONSOLE_PROMPTS, isOperatorSurface, resolveConsole } from './assistantConsole'

export const SURFACE_LABEL: Record<AssistantSurface, string> = {
  home: 'Home',
  discover: 'Discover',
  academics: 'Academics',
  housing: 'Housing',
  community: 'Community',
  opportunities: 'Opportunities',
  marketplace: 'Marketplace',
  money: 'Money',
  support: 'Support',
  profile: 'Profile',
  notes: 'Notes',
  services: 'Academic services',
  social: 'Campus life',
  property: 'the property console',
  institution: 'the institution console',
  provider: 'your provider console',
  author: 'your author console',
  employer: 'the employer console',
  merchant: 'the merchant console',
  representative: 'your representative console',
  admin: 'the admin console',
  operator: 'this console',
}

const SUGGESTIONS: Record<AssistantSurface, string[]> = {
  home: [
    'What needs my attention this week?',
    'Which of my saved universities have applications closing this month?',
    'Find me a furnished room within 20 minutes of campus under my budget',
  ],
  discover: [
    'Which of my saved universities have applications closing this month?',
    'What documents am I missing?',
    'Find someone to review my personal statement',
  ],
  academics: [
    'What am I registered for this semester?',
    'Which course am I furthest behind on?',
    'Find past questions for operations research',
  ],
  housing: [
    'Find me a furnished room within 20 minutes of campus under my budget',
    'Which listings near campus are verified?',
    'Pay my rent for September',
  ],
  community: [
    'Which groups should I join?',
    'Find a tutor for engineering mathematics',
    'Split ₦20,000 between four people',
  ],
  opportunities: [
    'Which roles match my profile?',
    'What stage are my applications at?',
    'Find someone to review my CV',
  ],
  marketplace: [
    'Find a tutor for engineering mathematics',
    'What are my open orders?',
    'Which resources match my courses?',
  ],
  money: [
    'What is my available balance?',
    'Split ₦20,000 between four people',
    'Pay my rent for September',
  ],
  support: ['What needs my attention this week?', 'Which groups should I join?'],
  profile: ['What signals is personalization using?', 'Which roles match my profile?'],
  notes: ['What needs my attention this week?', 'Find past questions for operations research'],
  services: ['What documents am I missing?', 'What needs my attention this week?'],
  social: ['Which groups should I join?', 'What needs my attention this week?'],
  property: [],
  institution: [],
  provider: [],
  author: [],
  employer: [],
  merchant: [],
  representative: [],
  admin: [],
  operator: [],
}

export function suggestedPrompts(surface: AssistantSurface): string[] {
  // Operator consoles carry their own prompts, because a property manager and a
  // student have no question in common.
  if (isOperatorSurface(surface)) return CONSOLE_PROMPTS[surface]
  return SUGGESTIONS[surface]
}

function has(query: string, ...terms: string[]): boolean {
  return terms.some((term) => query.includes(term))
}

/**
 * Resolves a question against authorized Campuzon data only.
 *
 * There is no model inference here: the Havazon AI seam would sit in front of
 * this resolver, and would still be constrained to the same records. Anything
 * the platform cannot source is refused rather than invented.
 */
export function resolveAssistant(raw: string, surface: AssistantSurface): AssistantAnswer {
  const query = raw.trim().toLowerCase()

  // An operator console is answered from that console's records only. Falling
  // through to the student resolver would answer a landlord from a student's
  // housing search, which is a privacy failure, not a convenience.
  if (isOperatorSurface(surface)) return resolveConsole(query, surface)

  // ---- Refusals first, so unsupported questions never fall through to a guess.
  if (has(query, 'exam policy', 'policy on', 'regulation', 'senate', 'handbook')) {
    return {
      text: 'I cannot answer that from Campuzon data.',
      cards: [],
      provenance: 'derived',
      refusal: {
        reason:
          'Institutional policies are not published to Campuzon by your university, so I have no authoritative source for them.',
        whereToLook:
          'Your faculty office holds the current regulations. If your university enables full integration, verified policies would appear in Academics.',
      },
    }
  }

  if (has(query, 'what grade will', 'predict my', 'my final grade', 'will i pass')) {
    return {
      text: 'I will not estimate that.',
      cards: [],
      provenance: 'derived',
      refusal: {
        reason:
          'Grades are institutional records. A prediction would look like one without being one, so I do not produce them.',
        whereToLook: 'Results in Academics shows every grade your university has released.',
      },
    }
  }

  if (has(query, 'phone number of', 'address of', 'where does', 'housing profile of')) {
    return {
      text: 'That belongs to someone else.',
      cards: [],
      provenance: 'derived',
      refusal: {
        reason:
          'I only return what a person has chosen to share with you. Contact details, housing profiles and academic records stay with their owner.',
        whereToLook:
          'Their community profile shows the fields they made visible to you, and you can message them from there.',
      },
    }
  }

  // ---- Money: composed actions require the user to confirm.
  if (has(query, 'pay my rent', 'pay rent', 'rent payment')) {
    return {
      text: 'I have prepared the rent payment. Nothing moves until you confirm it.',
      cards: [],
      provenance: 'derived',
      confirmation: {
        id: 'cf-rent',
        action: 'Rent payment',
        summary: 'Cedarwood Residence · Room B-204 · September instalment',
        lines: [
          { label: 'Amount', value: naira(180000) },
          { label: 'From', value: 'Available balance' },
          { label: 'Reference', value: 'RENT-2026-09-B204' },
        ],
        gated: true,
        gateNote:
          'Payment execution sits with a licensed payment institution and is not yet enabled. Confirming records your intent only.',
        confirmLabel: 'Confirm rent payment',
      },
    }
  }

  if (has(query, 'split')) {
    const amount = Number(query.replace(/[^0-9]/g, '')) || 20000
    const people = 4
    return {
      text: `Split prepared — ${naira(amount)} across ${people} people.`,
      cards: [],
      provenance: 'derived',
      confirmation: {
        id: 'cf-split',
        action: 'Expense split',
        summary: `${naira(amount)} divided evenly, each person receives a request`,
        lines: [
          { label: 'Each share', value: naira(Math.round(amount / people)) },
          { label: 'Requests to', value: '@ifeoma.e · @danielu · @tolua' },
          { label: 'Your share', value: naira(Math.round(amount / people)) },
        ],
        gated: true,
        gateNote:
          'Requests can be recorded, but settlement between users requires the licensed transfer provider.',
        confirmLabel: 'Send the requests',
      },
    }
  }

  if (has(query, 'balance', 'how much do i have')) {
    const balances = deriveBalances(transactions)
    const cards: AssistantCard[] = [
      {
        id: 'bal-available',
        kind: 'balance',
        title: `Available — ${naira(balances.available)}`,
        meta: 'Derived from your ledger entries, not a stored number',
        detail: `Pending ${naira(balances.pending)} · Escrow ${naira(balances.escrow)} · Withdrawable ${naira(balances.withdrawable)}`,
        to: '/money',
        badge: { label: 'Records only', tone: 'warn' },
        provenance: 'derived',
      },
    ]
    return {
      text: 'Every balance is computed from your transaction history.',
      cards,
      provenance: 'derived',
    }
  }

  // ---- Housing search.
  if (has(query, 'room', 'hostel', 'apartment', 'housing', 'furnished', 'listing', 'live')) {
    const budget = defaultPreferences.budgetMax
    const minutesMatch = query.match(/(\d+)\s*(minute|min)/)
    const maxMinutes = minutesMatch ? Number(minutesMatch[1]) : 25
    const wantsFurnished = has(query, 'furnished')
    const wantsVerified = has(query, 'verified')

    const matched = listings
      .filter((listing) => listing.minutesToCampus <= maxMinutes)
      .filter((listing) => (wantsFurnished ? listing.facilities.includes('Furnished') : true))
      .filter((listing) => (wantsVerified ? listing.verification === 'verified' : true))
      .filter((listing) => listing.rentPerYear <= budget)
      .slice(0, 4)

    if (matched.length === 0) {
      return {
        text: 'Nothing in the marketplace matches all of that right now.',
        cards: [],
        provenance: 'derived',
        refusal: {
          reason: `No listing is under ${naira(budget, true)}, within ${maxMinutes} minutes and matches every facility you named.`,
          whereToLook:
            'Housing lets you relax one filter at a time and shows how many results each change returns.',
        },
      }
    }

    return {
      text: `${matched.length} listing${matched.length === 1 ? '' : 's'} within ${maxMinutes} minutes and under ${naira(budget, true)}.`,
      cards: matched.map((listing) => ({
        id: listing.id,
        kind: 'listing' as const,
        title: listing.name,
        meta: `${naira(listing.rentPerYear)} / year · ${listing.minutesToCampus} min · ${listing.roomType}`,
        detail: `${listing.neighbourhood} · ${listing.bedsAvailable} bed${listing.bedsAvailable === 1 ? '' : 's'} available`,
        to: `/housing/${listing.id}`,
        badge:
          listing.verification === 'verified'
            ? { label: 'Verified', tone: 'ok' as const }
            : listing.verification === 'pending'
              ? { label: 'Verification pending', tone: 'warn' as const }
              : { label: 'Not inspected by Campuzon', tone: 'warn' as const },
        provenance: 'verified' as const,
      })),
      provenance: 'derived',
    }
  }

  // ---- Admissions: deadlines and outstanding documents.
  if (has(query, 'closing', 'deadline', 'saved universit', 'application')) {
    return {
      text: `${admissionApplications.length} applications in your workspace, ordered by deadline.`,
      cards: admissionApplications.map((application) => {
        const programme = programmeById(application.programmeId)
        const university = universityProfileById(application.universityId)
        const outstanding = application.requirements.filter((item) => !item.met).length
        return {
          id: application.id,
          kind: 'application' as const,
          title: programme?.title ?? 'Programme',
          meta: `${university?.shortName ?? 'University'} · ${application.deadlineLabel}`,
          detail:
            outstanding > 0
              ? `${outstanding} requirement${outstanding === 1 ? '' : 's'} outstanding`
              : 'All requirements met',
          to: '/discover',
          badge: { label: application.stage, tone: 'brand' as const },
          provenance: 'verified' as const,
        }
      }),
      provenance: 'derived',
    }
  }

  if (has(query, 'document', 'missing')) {
    const cards: AssistantCard[] = []
    admissionApplications.forEach((application) => {
      const programme = programmeById(application.programmeId)
      application.documents
        .filter((document) => document.status === 'missing')
        .forEach((document) => {
          cards.push({
            id: `${application.id}-${document.label}`,
            kind: 'requirement',
            title: document.label,
            meta: programme?.title ?? 'Application',
            detail: `Needed before ${application.deadlineLabel}`,
            to: '/discover',
            badge: { label: 'Missing', tone: 'warn' },
            provenance: 'verified',
          })
        })
    })

    if (cards.length === 0) {
      return {
        text: 'Nothing is outstanding — every document on your applications is uploaded.',
        cards: [],
        provenance: 'derived',
      }
    }

    return {
      text: `${cards.length} document${cards.length === 1 ? '' : 's'} still missing across your applications.`,
      cards,
      provenance: 'derived',
    }
  }

  // ---- Marketplace: providers and orders.
  if (has(query, 'tutor', 'mentor', 'review my', 'find someone', 'coach', 'provider')) {
    const need = has(query, 'cv')
      ? 'cv'
      : has(query, 'statement')
        ? 'statement'
        : has(query, 'maths', 'mathematics')
          ? 'mathematics'
          : ''

    const matched = serviceProviders
      .filter((provider) =>
        need
          ? `${provider.headline} ${provider.subcategory} ${provider.skills.join(' ')}`
              .toLowerCase()
              .includes(need)
          : true,
      )
      .slice(0, 4)

    const pool = matched.length > 0 ? matched : serviceProviders.slice(0, 3)

    return {
      text:
        matched.length > 0
          ? `${matched.length} provider${matched.length === 1 ? '' : 's'} offer this.`
          : 'No exact match, so here are the closest verified providers.',
      cards: pool.map((provider) => ({
        id: provider.id,
        kind: 'provider' as const,
        title: provider.displayName,
        meta: `@${provider.handle} · ${provider.subcategory} · from ${naira(
          Math.min(...provider.packages.map((item) => item.priceNaira)),
        )}`,
        detail: provider.headline,
        to: '/marketplace',
        badge: provider.identityVerified
          ? { label: 'Identity verified', tone: 'ok' as const }
          : { label: 'Unverified', tone: 'warn' as const },
        provenance: 'verified' as const,
      })),
      provenance: 'derived',
    }
  }

  if (has(query, 'order', 'escrow', 'delivery')) {
    return {
      text: `${myOrders.length} order${myOrders.length === 1 ? '' : 's'} on your account.`,
      cards: myOrders.map((order) => ({
        id: order.id,
        kind: 'order' as const,
        title: order.title,
        meta: `${order.reference} · ${order.counterparty} · ${naira(order.amountNaira)}`,
        detail: order.dueLabel,
        to: '/marketplace',
        badge: { label: orderStateLabel(order.state), tone: 'brand' as const },
        provenance: 'verified' as const,
      })),
      provenance: 'derived',
    }
  }

  // ---- Academics and learning.
  if (has(query, 'registered', 'semester', 'course', 'behind', 'quiz', 'progress', 'study')) {
    const registered = courses.filter((course) => course.status === 'registered')
    return {
      text: `${registered.length} registered courses this semester.`,
      cards: registered.map((course) => ({
        id: course.id,
        kind: 'course' as const,
        title: `${course.code} — ${course.title}`,
        meta: `${course.units} units · ${course.lecturer}`,
        detail: 'Materials, lectures, quizzes and study groups',
        to: '/academics',
        badge:
          course.source === 'synced'
            ? { label: 'Directory synced', tone: 'ok' as const }
            : { label: 'Entered by you', tone: 'neutral' as const },
        provenance: course.source === 'synced' ? ('verified' as const) : ('user' as const),
      })),
      provenance: 'derived',
    }
  }

  // ---- Opportunities.
  if (has(query, 'job', 'role', 'internship', 'match my profile', 'graduate')) {
    const matched = opportunities.slice(0, 4)
    return {
      text: `${matched.length} open roles you are eligible for.`,
      cards: matched.map((opportunity) => ({
        id: opportunity.id,
        kind: 'role' as const,
        title: opportunity.title,
        meta: `${opportunity.employer} · ${opportunity.location} · ${opportunity.stipend}`,
        detail: `${opportunity.kind} · ${opportunity.deadlineLabel}`,
        to: '/opportunities',
        badge: opportunity.employerVerified
          ? { label: 'Verified employer', tone: 'ok' as const }
          : { label: 'Unverified employer', tone: 'warn' as const },
        provenance: 'verified' as const,
      })),
      provenance: 'derived',
    }
  }

  // ---- Community.
  if (has(query, 'group', 'club', 'join')) {
    const open = groups.filter((group) => !group.joined).slice(0, 4)
    return {
      text: `${open.length} groups you are not yet a member of.`,
      cards: open.map((group) => ({
        id: group.id,
        kind: 'group' as const,
        title: group.name,
        meta: `${group.members.toLocaleString()} members · ${group.kind}`,
        detail: group.description,
        to: '/community',
        badge: { label: group.privacy, tone: 'neutral' as const },
        provenance: 'verified' as const,
      })),
      provenance: 'derived',
    }
  }

  // ---- This week.
  if (has(query, 'attention', 'this week', 'today', 'due')) {
    const cards: AssistantCard[] = [
      ...admissionApplications.slice(0, 2).map((application) => ({
        id: `att-${application.id}`,
        kind: 'application' as const,
        title: programmeById(application.programmeId)?.title ?? 'Application',
        meta: application.deadlineLabel,
        detail: `${application.requirements.filter((item) => !item.met).length} requirements outstanding`,
        to: '/discover',
        badge: { label: application.stage, tone: 'brand' as const },
        provenance: 'verified' as const,
      })),
      ...myOrders
        .filter((order) => order.state === 'DELIVERED' || order.state === 'BUYER_REVIEW')
        .slice(0, 2)
        .map((order) => ({
          id: `att-${order.id}`,
          kind: 'order' as const,
          title: order.title,
          meta: `${order.counterparty} · awaiting your review`,
          detail: `${naira(order.amountNaira)} held in escrow until you approve`,
          to: '/marketplace',
          badge: { label: orderStateLabel(order.state), tone: 'warn' as const },
          provenance: 'verified' as const,
        })),
    ]

    return {
      text: 'Here is what is waiting on you.',
      cards,
      provenance: 'derived',
    }
  }

  // ---- Nothing matched.
  return {
    text: 'I could not resolve that against your Campuzon data.',
    cards: [],
    provenance: 'derived',
    refusal: {
      reason:
        'I only answer from records this account is authorized to read — your academics, housing, applications, orders, ledger, groups and opportunities.',
      whereToLook: `Try one of the suggestions below, or search ${SURFACE_LABEL[surface]} directly.`,
    },
  }
}
